import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Paperclip, UploadSimple, Trash, FileImage, FilePdf, File as FileIcon,
  Spinner, ArrowSquareOut, Info, PencilSimple, Check as CheckIcon, X as XIcon
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 20 * 1024 * 1024; // 20MB

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '-';
  const u = ['B', 'KB', 'MB', 'GB'];
  let s = bytes;
  for (let i = 0; i < u.length; i++) {
    if (s < 1024) return `${s.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
    s /= 1024;
  }
  return `${s.toFixed(1)} TB`;
};

const fileIcon = (mime = '') => {
  if (mime.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf') return FilePdf;
  return FileIcon;
};

/**
 * Inline attachment manager rendered directly inside the quotation create/edit form.
 * - When `documentId` exists (editing): live list with upload + remove
 * - When `documentId` is null (creating): renders a friendly notice
 */
export default function QuotationAttachmentField({ documentId, documentType = 'quotation' }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // ===== Inline rename (custom_name) — restores editable filenames on
  // quotations, POs, GRNs and invoices. PATCH /api/documents/attachments/{id}
  // with { custom_name } already exists on the backend; this UI wires it in.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const displayName = (att) => (att?.custom_name || att?.original_filename || '');
  const startRename = (att) => { setRenamingId(att.id); setRenameDraft(displayName(att)); };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(''); };
  const commitRename = async (att) => {
    const name = (renameDraft || '').trim();
    if (!name || name === displayName(att)) { cancelRename(); return; }
    setRenameSaving(true);
    try {
      await axios.patch(`${API_URL}/documents/attachments/${att.id}`, { custom_name: name });
      setAttachments(prev => prev.map(a => a.id === att.id ? { ...a, custom_name: name } : a));
      toast.success('Renamed');
      cancelRename();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      toast.error(typeof raw === 'string' ? raw : (raw?.message || 'Failed to rename'));
    } finally {
      setRenameSaving(false);
    }
  };

  const endpoint = documentType === 'quotation' ? 'quotations'
    : documentType === 'po' ? 'purchase_orders'
    : documentType === 'grn' ? 'grns' : 'invoices';

  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/documents/attachments/${endpoint}/${documentId}`);
        if (!cancelled) setAttachments(res.data.attachments || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load attachments');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [documentId, endpoint]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (!documentId) {
      toast.error('Save the quotation first, then attach files');
      return;
    }
    const valid = files.filter(f => {
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} is too large (max 20MB)`);
        return false;
      }
      return true;
    });
    if (!valid.length) return;
    setUploading(true);
    try {
      for (const file of valid) {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(
          `${API_URL}/documents/attachments/${endpoint}/${documentId}`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      const res = await axios.get(`${API_URL}/documents/attachments/${endpoint}/${documentId}`);
      setAttachments(res.data.attachments || []);
      toast.success(`${valid.length} file${valid.length > 1 ? 's' : ''} attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Remove this attachment? Cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/documents/attachments/${attachmentId}`);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Attachment removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove attachment');
    }
  };

  const handleView = async (attachmentId) => {
    try {
      const res = await axios.get(
        `${API_URL}/documents/attachments/file/${attachmentId}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Failed to open file');
    }
  };

  if (!documentId) {
    return (
      <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="attachment-create-hint">
        <Info size={18} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-medium text-amber-700 dark:text-amber-300">Attach files (drawings, images, PDFs) after saving</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Save this quotation first — once it has a reference number, you can attach scope drawings, images, and supporting PDFs here.
            They'll be listed on the PDF as a "Schedule of Attachments" and embedded inline if they're images.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid="attachment-field">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <Paperclip size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium">Attachments</span>
          <span className="text-xs text-muted-foreground">({attachments.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.dwg"
            onChange={handleFiles}
            className="hidden"
            data-testid="attachment-file-input"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="attachment-upload-btn"
          >
            {uploading ? <Spinner size={14} className="animate-spin mr-1.5" /> : <UploadSimple size={14} className="mr-1.5" />}
            {uploading ? 'Uploading…' : 'Add files'}
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Spinner size={18} className="animate-spin mr-2" /> Loading…
        </div>
      ) : attachments.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground">
          No attachments yet. Add drawings, images, or supporting PDFs (max 20MB each).
        </div>
      ) : (
        <ul className="divide-y">
          {attachments.map((att) => {
            const Icon = fileIcon(att.file_type);
            const shownName = displayName(att);
            const isRenaming = renamingId === att.id;
            return (
              <li key={att.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30" data-testid={`attachment-row-${att.id}`}>
                <Icon size={20} className="text-[#d4a84b] shrink-0" weight="fill" />
                <div className="flex-1 min-w-0">
                  {isRenaming ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(att); }
                          else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                        autoFocus
                        disabled={renameSaving}
                        className="h-7 text-sm"
                        data-testid={`rename-input-${att.id}`}
                      />
                      <Button type="button" size="sm" variant="ghost" onClick={() => commitRename(att)} disabled={renameSaving} className="h-7 px-2 text-emerald-600" title="Save" data-testid={`rename-save-${att.id}`}>
                        {renameSaving ? <Spinner size={14} className="animate-spin" /> : <CheckIcon size={14} />}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={cancelRename} disabled={renameSaving} className="h-7 px-2" title="Cancel" data-testid={`rename-cancel-${att.id}`}>
                        <XIcon size={14} />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 group">
                      <div
                        className="text-sm font-medium truncate cursor-pointer hover:underline"
                        onClick={() => startRename(att)}
                        title="Click to rename"
                        data-testid={`att-name-${att.id}`}
                      >
                        {shownName}
                      </div>
                      <button
                        type="button"
                        onClick={() => startRename(att)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary shrink-0"
                        title="Rename"
                        data-testid={`rename-att-${att.id}`}
                      >
                        <PencilSimple size={12} />
                      </button>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <span>{formatSize(att.file_size)}</span>
                    <span>·</span>
                    <span>{att.uploaded_by_name || 'Unknown'}</span>
                    {att.custom_name && att.original_filename && att.custom_name !== att.original_filename && (
                      <span className="italic opacity-70 truncate">· orig: {att.original_filename}</span>
                    )}
                  </div>
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={() => handleView(att.id)} title="Open in new tab" data-testid={`attachment-open-${att.id}`}>
                  <ArrowSquareOut size={14} />
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => handleDelete(att.id)} className="text-red-500 hover:text-red-600" title="Remove" data-testid={`attachment-delete-${att.id}`}>
                  <Trash size={14} />
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Footer hint */}
      <div className="px-4 py-2 bg-muted/20 border-t text-[11px] text-muted-foreground">
        Image attachments (PNG/JPG) are embedded as inline pages on the quotation PDF. Other files are listed in a Schedule of Attachments.
      </div>
    </div>
  );
}
